import type { Dispatch, SetStateAction } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { WovarProduct } from "@commercekit/api";
import type { ActionData } from "~/lib/help/type";
import { CartLineItemActions } from "~/lib/action/actions";
import { disabledReload } from "~/lib/utils";
import type { Fetcher } from "@remix-run/react";
import { useFetchers } from "@remix-run/react";
import { useList, useLocation } from "react-use";
import { useLocalePath } from "./useLocale";
import { useTypedStateFetcher } from "./useTypedStateFetcher";
import { useCheckout, useCheckoutSimple } from "./useCheckout";

export interface CartCtxValue {
  addToCart: (product: WovarProduct & { quantity: number; uomProperty?: string }) => void;
  loadingQueue: (WovarProduct & { quantity: number; uomProperty?: string })[];
  resultQueue: (WovarProduct & {
    quantity: number;
    uomProperty?: string;
    actionFail: boolean;
    error: string | null;
  })[];
  clearResult: () => void;
  cartLoading: boolean;
}
const initValue: CartCtxValue = {
  addToCart: () => {},
  loadingQueue: [],
  resultQueue: [],
  clearResult: () => {},
  cartLoading: false,
};

export const CartCtx = createContext<CartCtxValue>(initValue);

export const CartProvider = ({ children }: React.PropsWithChildren) => {
  const { pathname } = useLocation();
  const { checkout: checkoutSimple, updateTempCartSimple } = useCheckoutSimple();
  const { checkout, updateTempCart } = useCheckout();
  const [readyQueue, readyAction] = useList<WovarProduct & { quantity: number; uomProperty?: string }>();
  const [pendingQueue, pendingAction] = useList<WovarProduct & { quantity: number; uomProperty?: string }>();
  const [finishQueue, finishAction] = useList<WovarProduct & { quantity: number; uomProperty?: string }>();
  const single = useTypedStateFetcher<ActionData<{ lineItems: string }>>();
  const multi = useTypedStateFetcher<ActionData<{ lineItems: string }>>();
  const [failObject, setFailObject] = useState<Record<string, boolean>>({});
  const [errorObject, setErrorObject] = useState<Record<string, string | null>>({});
  const locale = useLocalePath();
  useEffect(() => {
    if (single.isActionSubmission) {
      setFailObject({});
      setErrorObject({});
    }
  }, [single.isActionSubmission]);
  useHandleError(single, setFailObject, setErrorObject);
  useHandleError(multi, setFailObject, setErrorObject);
  const loading = useMemo(() => single.state !== "idle" || multi.state !== "idle", [single.state, multi.state]);
  useEffect(() => {
    if (readyQueue.length && pendingQueue.length === 0 && !loading) {
      const [target, options] = postBody(readyQueue.slice(0, 1), locale("/cart"));
      single.submit(target, options);
      pendingAction.push(readyQueue[0]);
      readyAction.clear();
    }
  }, [readyQueue, pendingQueue, pendingAction, readyAction, single, loading, locale]);
  useEffect(() => {
    if (readyQueue.length && pendingQueue.length && !loading) {
      multi.submit(...postBody(readyQueue, locale("/cart")));
      pendingAction.push(...readyQueue);
      readyAction.clear();
    }
  }, [readyQueue, pendingQueue, loading, multi, pendingAction, readyAction, locale]);
  useEffect(() => {
    if (!loading && pendingQueue.length && !readyQueue.length) {
      finishAction.push(...pendingQueue);
      pendingAction.clear();
      sessionStorage.removeItem("tempCart");
      if (pathname === "/cart") updateTempCart(null);
      else updateTempCartSimple(null);
    }
  }, [loading, pendingQueue, finishAction, pendingAction, readyQueue]);
  const clearResult = useCallback(() => finishAction.clear(), [finishAction]);
  const loadingQueue = useMemo(() => [...readyQueue, ...pendingQueue], [readyQueue, pendingQueue]);
  const fetchers = useFetchers();
  const addToCart = useCallback(
    (item: WovarProduct & { quantity: number; uomProperty?: string }) => {
      const {
        id,
        quantity,
        priceInclTax: { currencyCode, value },
        salePriceInclTax,
        priceExclTax: { value: valueExclTax },
        salePriceExclTax,
        originalTotal,
        originalPrice,
        defaultImage: { urlThumbnail: url, altText },
        unitsOfMeasure,
        slug,
        uomProperty,
      } = item;
      const primaryUom = unitsOfMeasure.find((v) => v.primary);
      let secondaryUom = unitsOfMeasure.find((v) => !v.primary && v.uomProperty === uomProperty);
      const uom = uomProperty ?? primaryUom.uomProperty;
      const formattedItem = {
        id: `${id}-${uom}`,
        quantity,
        [pathname === "/cart" ? "totalInclTax" : "total"]: {
          currencyCode,
          value:
            (secondaryUom
              ? secondaryUom.salePriceInclTax?.value ?? secondaryUom.priceInclTax.value
              : salePriceInclTax?.value ?? value) * quantity,
        },

        ...(pathname === "/cart"
          ? {
              totalExclTax: {
                currencyCode,
                value:
                  (secondaryUom
                    ? secondaryUom.salePriceExclTax?.value ?? secondaryUom.priceExclTax.value
                    : salePriceExclTax?.value ?? valueExclTax) * quantity,
              },
              priceInclTax: {
                currencyCode,
                value: secondaryUom
                  ? secondaryUom.salePriceInclTax?.value ?? secondaryUom.priceInclTax.value
                  : salePriceInclTax?.value ?? value,
              },
              priceExclTax: {
                currencyCode,
                value: secondaryUom
                  ? secondaryUom.salePriceExclTax?.value ?? secondaryUom.priceExclTax.value
                  : salePriceExclTax?.value ?? valueExclTax,
              },
            }
          : {}),
        originalTotal,
        originalPrice,
        image: { url, altText },
        deliveryEstimateMessage: primaryUom.availability.deliveryEstimateMessage,
        uomProperty: uom,
        product: { slug, unitsOfMeasure },
        valid: true,
      };
      const tempCart = sessionStorage.getItem("tempCart");
      const tempCartObj = tempCart
        ? JSON.parse(tempCart)
        : (pathname === "/cart" ? checkout : checkoutSimple) ?? { lineItems: [], subtotalInclTax: { currencyCode, value: 0 } };
      const existingItem = tempCartObj.lineItems.find(
        (v) => v.product.slug === formattedItem.product.slug && v.uomProperty === formattedItem.uomProperty
      );
      if (existingItem) {
        existingItem.quantity += formattedItem.quantity;
        if (pathname === "/cart") existingItem.totalInclTax.value += formattedItem.totalInclTax.value;
        else existingItem.total.value += formattedItem.total.value;

        tempCartObj.lineItems[tempCartObj.lineItems.indexOf(existingItem)] = existingItem;
      } else tempCartObj.lineItems.push(formattedItem);

      if (pathname === "/cart") updateTempCart(tempCartObj);
      else updateTempCartSimple(tempCartObj);
      sessionStorage.setItem("tempCart", JSON.stringify(tempCartObj));
      readyAction.push(item);
    },
    [readyAction, checkoutSimple, checkout, pathname]
  );
  const resultQueue = useMemo(
    () =>
      finishQueue.map((i) => ({
        ...i,
        actionFail: failObject[i.sku],
        error: errorObject[i.sku],
      })),
    [failObject, finishQueue, errorObject]
  );
  const cartLoading = useMemo(
    () => fetchers.some((i) => i.formAction === locale("/cart") && (i.state === "submitting" || i.state === "loading")),
    [fetchers, locale]
  );
  return (
    <CartCtx.Provider
      value={{
        addToCart,
        loadingQueue,
        cartLoading,
        resultQueue,
        clearResult,
      }}
    >
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);

function postBody(lineItems: (WovarProduct & { quantity: number; uomProperty?: string })[], action: string) {
  const formData = disabledReload().set(new FormData());
  formData.set("_action", CartLineItemActions.ADD);
  formData.set(
    "lineItems",
    JSON.stringify(
      lineItems.map(({ sku, quantity, uomProperty }) => ({
        sku,
        quantity,
        uomProperty: uomProperty ?? "",
      }))
    )
  );
  return [formData, { method: "post", action }] as const;
}

function useHandleError(
  fetcher: Fetcher<ActionData<{ lineItems: string }>>,
  setFailObject: Dispatch<SetStateAction<Record<string, boolean>>>,
  setErrorObject: Dispatch<SetStateAction<Record<string, string | null>>>
) {
  return useEffect(() => {
    if (fetcher.data?.errors && fetcher.data?.errors.length) {
      setFailObject((oldV) => {
        let lineItems: {
          sku: string;
          quantity: number;
          uomProperty?: string;
        }[] = [];
        try {
          lineItems = JSON.parse(fetcher.data?.value.lineItems || "[]");
        } catch (e) {}
        const newV: Record<string, boolean> = {};
        lineItems.forEach(({ sku }) => {
          newV[sku] = true;

          setErrorObject((oldErr) => {
            return {
              ...oldErr,
              [sku]: fetcher?.data?.errors.join("; "),
            };
          });
        });

        return {
          ...oldV,
          ...newV,
        };
      });
    }
  }, [fetcher.data, fetcher.data?.errors, setFailObject, setErrorObject]);
}
